import React from 'react';
import clsx from 'clsx';

import { Autocomplete, Input } from '@shared/components';

import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';

import CreateIcon from '@material-ui/icons/Create';
import AddIcon from '@assets/icons/plus-grey.svg';
import AddIconBlue from '@assets/icons/plus-blue.svg';
import FilterIcon from '@assets/icons/filter.svg';
import DeleteIcon from '@assets/icons/trash.svg';

import useStyles from './style';

const noop = () => {};

const defaultRow = {
  keyId: Date.now(),
  label: '',
  type: '',
  value: '',
  isFilterApplied: false,
};

const STRING_TYPE_LIST = {
  Contains: 'iRegexp',
  Equals: 'iLike',
  'Does not equal': 'notILike',
  'Does not contain': 'notIRegexp',
};

const NUM_TYPE_LIST = {
  Equals: 'iLike',
  'Does not equal': 'notILike',
};

const defaultState = {
  isFiltering: false,
  filterRows: [defaultRow],
  columnsList: [],
};
const Filter = ({
  buttonLabel = 'Filter',
  filterConfig = [],
  onFilterApply = noop,
  appliedFilters = [],
  disabled = false,
}) => {
  const classes = useStyles();
  const [anchorRef, setAnchorRef] = React.useState(null);
  const [state, setState] = React.useState(defaultState);

  const handleChange = (rowIndex, type = '', value = '') => {
    const row = { ...state.filterRows[rowIndex] };
    row[type] = value;
    if (type === 'label') {
      row.type = '';
      row.value = '';
    }
    if (type === 'type') {
      row.value = '';
    }

    setState(prevState => {
      const filterRows = prevState.filterRows.slice();
      filterRows.splice(rowIndex, 1, row);
      return {
        ...prevState,
        filterRows,
      };
    });
  };

  const handleCancel = () => {
    setAnchorRef(null);
    setState(prevState => {
      let updatedRows = prevState.filterRows?.filter(
        row => row.isFilterApplied
      );
      updatedRows = updatedRows.length ? updatedRows : defaultState.filterRows;
      return {
        ...prevState,
        isFiltering: false,
        filterRows: updatedRows,
      };
    });
  };

  const handleDeleteRow = rowIndex => {
    setState(prevState => {
      const { filterRows } = prevState;
      filterRows.splice(rowIndex, 1);
      const updatedRows = prevState.filterRows;
      return {
        ...prevState,
        filterRows: updatedRows,
      };
    });
  };

  const handleAddClick = () => {
    const { filterRows } = state;
    filterRows.push({
      ...defaultRow,
      keyId: Date.now(),
    });
    setState(prevState => {
      const updatedRows = prevState.filterRows;
      return {
        ...prevState,
        filterRows: updatedRows,
      };
    });
  };

  const validateRows = () => {
    const { filterRows } = state;
    const isRowEmpty = filterRows.find(
      row => !row.label || !row.type || !row.value
    );
    return !!isRowEmpty;
  };

  const handleApplyFilter = (filters = []) => {
    if (!filters.length) {
      onFilterApply([], []);
    }
    const filterArr = [];
    filters.map(item => {
      const columnField = state.columnsList.find(
        row => row.columnName === item.label
      );
      const columns =
        columnField?.columnType === 'string' ? STRING_TYPE_LIST : NUM_TYPE_LIST;
      const option = columnField?.options?.find(
        opt => opt.label === item.value
      );
      const value = option ? option.value : item.value;

      filterArr.push({
        field: columnField.columnValue,
        type: columns[item.type],
        label: value,
        value,
        key: `${value}_isAdvancedType`,
        isAdvancedType: true,
      });
    });
    onFilterApply(filterArr);
  };

  React.useEffect(() => {
    if (!state.isFiltering || !filterConfig.length) return;
    const updatedFilterConfig = filterConfig.map(item => {
      return {
        columnName: item.fieldToDisplay,
        columnType: item.operatorType,
        columnValue: item.field,
        options: item.options,
      };
    });
    setState(prevState => ({
      ...prevState,
      columnsList: updatedFilterConfig,
    }));
    if (appliedFilters.length) {
      const updatedRows = appliedFilters.map(item => {
        const columnField = updatedFilterConfig.find(
          row => row.columnValue === item.field
        );
        const columns =
          columnField.columnType === 'string'
            ? STRING_TYPE_LIST
            : NUM_TYPE_LIST;
        const type = Object.keys(columns).find(
          key => columns[key] === item.type
        );
        const label = columnField.columnName;
        const value = columnField?.options?.length
          ? columnField.options.find(option => option.value === item.value)
              ?.label
          : item.value;

        return {
          keyId: Date.now(),
          label,
          type,
          value,
          isFilterApplied: true,
        };
      });
      setState(prevState => ({
        ...prevState,
        filterRows: updatedRows,
      }));
    }
  }, [state.isFiltering]);

  const isDisabled = validateRows() || disabled;
  return (
    <>
      <Button
        className='mt-4 mb-4'
        variant='outlined'
        color='primary'
        startIcon={<CreateIcon />}
        onClick={event => {
          if (state.isFiltering) {
            handleCancel();
          } else {
            setAnchorRef(event.currentTarget);
            setState(prevState => ({
              ...prevState,
              isFiltering: true,
            }));
          }
        }}
      >
        {buttonLabel}
      </Button>
      <div className={clsx(classes.paper)}>
        <Popper
          open={state.isFiltering}
          anchorEl={anchorRef}
          disablePortal
          className={clsx('popper-w-arrow', classes.advancedFilter)}
          placement='bottom-start'
          keepMounted
          modifiers={{
            hide: {
              enabled: false,
            },
            flip: {
              enabled: false,
            },
            preventOverflow: {
              enabled: false,
              boundariesElement: 'scrollParent',
            },
          }}
        >
          <span>
            <Paper
              elevation={4}
              className={clsx('p-6', classes.filterPaperWrapper)}
            >
              <div className={clsx('d-flex f-align-center f-justify-between')}>
                <Typography variant='body1' className='text-bold'>
                  Advanced Filter
                </Typography>
                <Button
                  color='primary'
                  onClick={() => {
                    setState(prevState => ({
                      ...prevState,
                      filterRows: [defaultRow],
                    }));
                    handleApplyFilter([]);
                  }}
                >
                  Clear All
                </Button>
              </div>
              {state.filterRows.map((row, index) => {
                const columnField = state.columnsList.find(
                  item => item.columnName === row.label
                );

                return (
                  <div className='d-flex mb-2 w-100'>
                    <Autocomplete
                      fullWidth
                      getOptionLabel={option => {
                        return option || '';
                      }}
                      disableClearable
                      className={clsx('mr-2', {
                        [classes.inputWidth]: state.filterRows.length > 1,
                      })}
                      value={row.label}
                      options={state.columnsList.map(item => item.columnName)}
                      onChange={(event, value) => {
                        handleChange(index, 'label', value);
                      }}
                      placeholder='Select..'
                    />
                    <Input
                      className={clsx('mr-2', {
                        [classes.inputWidth]: state.filterRows.length > 1,
                      })}
                      fullWidth
                      disabled={!row.label}
                      placeholder='Select..'
                      value={row.type}
                      options={Object.keys(
                        columnField?.columnType === 'string'
                          ? STRING_TYPE_LIST
                          : NUM_TYPE_LIST
                      ).map(item => {
                        return {
                          label: item,
                          value: item,
                          isSelected: item === row.type,
                        };
                      })}
                      onChange={selectedOption => {
                        handleChange(index, 'type', selectedOption.value);
                      }}
                    />
                    {columnField?.options ? (
                      <Input
                        fullWidth
                        disabled={!row.type || !row.label}
                        className={clsx({
                          [classes.inputWidth]: state.filterRows.length > 1,
                        })}
                        placeholder='Select..'
                        value={row.value}
                        options={columnField.options?.map(item => {
                          return {
                            label: item.label,
                            value: item.value,
                            isSelected: item.label === row.value,
                          };
                        })}
                        onChange={selectedOption => {
                          handleChange(index, 'value', selectedOption.label);
                        }}
                      />
                    ) : (
                      <Input
                        fullWidth
                        type='text'
                        disabled={!row.type || !row.label}
                        className={clsx({
                          [classes.inputWidth]: state.filterRows.length > 1,
                        })}
                        placeholder='Type something...'
                        value={row.value}
                        onChange={event => {
                          let value = event.currentTarget.value;
                          handleChange(index, 'value', value);
                        }}
                      />
                    )}

                    {state.filterRows.length > 1 && (
                      <IconButton onClick={() => handleDeleteRow(index)}>
                        <img src={DeleteIcon} alt='delete' />
                      </IconButton>
                    )}
                  </div>
                );
              })}
              <Button
                startIcon={
                  <img alt='' src={!isDisabled ? AddIconBlue : AddIcon} />
                }
                color='primary'
                onClick={handleAddClick}
                disabled={isDisabled}
              >
                <Typography variant='button' className='mt-1'>
                  Add Filter...
                </Typography>
              </Button>
              <div className='d-flex f-justify-end'>
                <Button
                  variant='outlined'
                  color='primary'
                  className='mr-2'
                  onClick={handleCancel}
                >
                  <Typography variant='button' className='pl-4 pr-4'>
                    Cancel
                  </Typography>
                </Button>
                <Button
                  variant='contained'
                  color='primary'
                  disabled={isDisabled}
                  onClick={() => {
                    setAnchorRef(null);
                    setState(prevState => ({
                      ...prevState,
                      isFiltering: false,
                    }));
                    handleApplyFilter(state.filterRows);
                  }}
                >
                  <Typography variant='button' className='pl-4 pr-4'>
                    Apply
                  </Typography>
                </Button>
              </div>
            </Paper>
          </span>
        </Popper>
      </div>
    </>
  );
};

{
  /* Filter.whyDidYouRender = true; */
}

export default Filter;
